import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {add, fetch, update} from '../services/ProductLineItemsService';
import {ProductLineItem} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {productLineItemsQueryKeys} from './productLineItemsQueryKeys';

export const useProductLineItem = (productId: string, productLineItemId?: string) => {
    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const queryClient = useQueryClient();

    // FETCH
    const productLineItemQuery = useQuery({
        queryKey: productLineItemsQueryKeys.detail(productLineItemId),
        queryFn: () => fetch(productLineItemId!),
        enabled: !!productLineItemId
    });

    // ['product-line-items'] is the root of the list AND the detail keys, so one
    // invalidation reaches both (§8).
    const invalidate = () => queryClient.invalidateQueries({queryKey: productLineItemsQueryKeys.all});

    // CREATE MUTATION. The input is whitelisted by the service (buildCreateProductLineItemInput).
    const createMutation = useMutation({
        mutationFn: (values: ProductLineItem) => add(productId, values),
        onSuccess: () => {
            notifySuccess(I18n.get('Product Line Item created'));
            invalidate();
        },
        onError: notifyError
    });

    // UPDATE MUTATION. The id comes from the hook argument (or the record being edited),
    // never from the form; the service whitelists the rest.
    const updateMutation = useMutation({
        mutationFn: (values: ProductLineItem) => {
            const id = productLineItemId ?? values.id;
            if (!id) throw new Error('productLineItemId is required parameter');
            return update(id, values);
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Product Line Item updated'));
            invalidate();
        },
        onError: notifyError
    });

    // Resolve true once the save lands and false when it is rejected, never throw: the
    // drawer awaits this and closes only on true, so a server-side rejection keeps the
    // operator's input on screen (§6, §7). The mutation's onError has already toasted.
    const settle = (run: Promise<unknown>) => run.then(() => true, () => false);

    return {
        record: productLineItemQuery.data,
        loading: productLineItemQuery.isLoading,
        isError: productLineItemQuery.isError,
        fetchRecord: productLineItemQuery.refetch,
        createRecord: (values: ProductLineItem) => settle(createMutation.mutateAsync(values)),
        updateRecord: (values: ProductLineItem) => settle(updateMutation.mutateAsync(values)),
        createMutation,
        updateMutation,
    };
};
